<template>
  <Announcement
    v-if="shouldShow"
    :pages="pages"
    tag="feature-improvement"
    @close="dismiss"
  />
</template>

<script setup lang="ts">
import policyWorkflowImage from '@/assets/img/policies/announcement_policy_workflow.png'
import Announcement from '@/components/generics/modals/Announcement.vue'
import { useI18n } from '@/hooks/useI18N'
import { useUserPermission } from '@/hooks/usePermission'
import { computed, ref } from 'vue'

/**
 * The one product announcement that is currently live (ENGMAN-428: the new
 * content policy edit view is now the default).
 *
 * There is only ever one at a time — replace the contents of this component
 * when the next announcement ships, and unmount it from `Layout.vue` when
 * there is none.
 */
const STORAGE_KEY = 'viewed-announcement-policyEditViewDefault'

/**
 * The announcement stops being news well before everyone has happened to log
 * in and dismiss it, so it expires on its own rather than depending on someone
 * remembering to remove it.
 */
const SHOW_UNTIL = new Date('2026-12-01T00:00:00Z')

const i18n = useI18n()

// Only the people who actually create and edit policies are affected by the
// change, so nobody else is interrupted by it.
const { canWrite: canEditPolicies } = useUserPermission('policies')

const hasBeenDismissed = ref(localStorage.getItem(STORAGE_KEY) !== null)
const hasExpired = new Date() >= SHOW_UNTIL

const shouldShow = computed(
  () => canEditPolicies.value && !hasBeenDismissed.value && !hasExpired
)

const pages = computed(() => [
  {
    title: i18n.t('policy.NEW_VIEW_ANNOUNCEMENT_TITLE'),
    description: i18n.t('policy.NEW_VIEW_ANNOUNCEMENT_DESCRIPTION'),
    image: policyWorkflowImage,
  },
])

const dismiss = () => {
  localStorage.setItem(STORAGE_KEY, 'true')
  hasBeenDismissed.value = true
}
</script>
